import { PlayerProperty, Save, Restore } from '../PlayerProperty';
import IPlayerData from '../../IPlayerData';
import { IPlayerState } from '../../../State/Player/IPlayerState';
import { restoreString, restoreStringifiedArray } from '../../ParseHelper';
import { ILuckyTurnipsState } from '../../../State/LuckyTurnips/ILuckyTurnipsState';
import { getShuffledLootOrder } from '../../../State/LuckyTurnips/LuckyTurnipsReducer';
import { STAR_CHEST_TIER_DATA, luckyTurnipsLoot } from '../../../Constants/Chests';
import { MathHelper } from '../../../Utility/MathHelper';
import { ArrayHelper } from '../../../Utility/ArrayHelper';
import { Immutable } from 'Utility/Types';

const luckyTurnipsStateKey = 'v1_44_0_luckyTurnipsState' as const;

type PlayerStateKey = 'luckyTurnipsState';
type PlayerDataKeys = typeof luckyTurnipsStateKey;
export default class LuckyTurnips implements PlayerProperty<PlayerStateKey, PlayerDataKeys> {
    public isChanged(playerStateData: IPlayerState, playerEndpointData: IPlayerData): boolean {
        return playerEndpointData[luckyTurnipsStateKey] !== this.save(playerStateData)[luckyTurnipsStateKey];
    }

    public restore(playerEndpointData: IPlayerData): Restore<PlayerStateKey> {
        return deserialize(restoreString(playerEndpointData[luckyTurnipsStateKey], null));
    }

    public save(playerStateData: IPlayerState): Save<PlayerDataKeys> {
        return {
            [luckyTurnipsStateKey]: serialize(playerStateData.luckyTurnipsState),
        };
    }
}

//

function serializeLootIndex(lootIndex: number, picked: boolean) {
    // if lootIndex is 0 it can't be negative so add 1
    return (lootIndex + 1) * (picked ? -1 : 1);
}

function deserializeLootIndex(serialized: number) {
    return {
        lootIndex: Math.abs(serialized) - 1,
        picked: serialized < 0,
    };
}

//

function serialize(luckyTurnipsState: Immutable<ILuckyTurnipsState> | null): string | null {
    if (!luckyTurnipsState) return null;
    return JSON.stringify([
        // First element of the array is the star chest tier
        luckyTurnipsState.starChestTier,
        // Rest are loot order with indication whether it's picked or not
        // Warning - this loses order of picking
        ...luckyTurnipsState.lootOrder.map((lootIndex, turnipIndex) => {
            const picked = luckyTurnipsState.pickedLootIndices.includes(lootIndex);
            return serializeLootIndex(lootIndex, picked);
        }),
    ]);
}

function deserialize(serialized: string | null): ILuckyTurnipsState | null {
    if (!serialized) return null;
    let parsed = restoreStringifiedArray<number>(serialized, null);
    if (!parsed || !Array.isArray(parsed)) return null;
    parsed = parsed.map(Number);

    // Get the star chest tier and remove it from the array, so only "loot indices" remain
    const starChestTier = parsed.shift()!;

    // Deserialize loot indices
    const lootOrder = new Array<number>();
    const pickedLootIndices = new Array<number>();
    for (const element of parsed) {
        const { lootIndex, picked } = deserializeLootIndex(element);
        lootOrder.push(lootIndex);
        if (picked) {
            pickedLootIndices.push(lootIndex);
        }
    }

    return validate({
        starChestTier,
        lootOrder,
        pickedLootIndices,
    });
}

function validate(luckyTurnipsState: Immutable<ILuckyTurnipsState>): ILuckyTurnipsState {
    // tier

    const starChestTier = Math.round(
        MathHelper.clamp(luckyTurnipsState.starChestTier || 0, 0, STAR_CHEST_TIER_DATA.length - 1)
    );

    // loot order

    const lootOrder = luckyTurnipsState.lootOrder
        .map(e => Math.round(e))
        .filter(isValidLootIndex)
        .filter(ArrayHelper.filterOnce);

    if (lootOrder.length < luckyTurnipsLoot.length) {
        const newLootOrder = getShuffledLootOrder();
        for (let i = 0; i < newLootOrder.length; i += 1) {
            if (!lootOrder.includes(newLootOrder[i])) {
                lootOrder.push(newLootOrder[i]);
                if (lootOrder.length === luckyTurnipsLoot.length) break;
            }
        }
    }

    // picked loot indices

    const pickedLootIndices = luckyTurnipsState.pickedLootIndices
        .map(e => Math.round(e))
        .filter(isValidLootIndex)
        .filter(ArrayHelper.filterOnce);

    const picksAllowed = STAR_CHEST_TIER_DATA[starChestTier].luckyTurnipPicks.length;
    if (pickedLootIndices.length > picksAllowed) {
        pickedLootIndices.length = picksAllowed;
    }

    //

    return {
        starChestTier,
        lootOrder,
        pickedLootIndices,
    };
}

function isValidLootIndex(lootIndex: number) {
    return lootIndex >= 0 && lootIndex < luckyTurnipsLoot.length;
}
